import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { toast } from "react-toastify";
import {
  approveExternalLab,
  deleteExternalLabDocument,
  getExternalLab,
  getExternalLabDocumentUrl,
  rejectExternalLab,
  resubmitExternalLab,
  returnExternalLabForCorrection,
  uploadExternalLabDocument,
} from "api/externalLabs";
import {
  DOCUMENT_ACCEPT,
  HISTORY_ACTION_LABELS,
  LAB_LIMITS,
  LAB_STATUS,
  documentFileError,
  formatDate,
  labActions,
} from "utils/externalLabRules";
import ExternalLabActionDialog from "./ExternalLabActionDialog";
import ExternalLabMappingSection from "./ExternalLabMappingSection";
import ExternalLabStatusChip from "./ExternalLabStatusChip";

const ACTIONS = {
  approve: {
    run: approveExternalLab,
    title: "Approve and activate this lab?",
    description:
      "The lab becomes Active and can then be mapped to hospitals / clinics.",
    confirmLabel: "Approve",
    confirmColor: "success",
    input: "note",
    done: "Lab approved and activated",
  },
  returnForCorrection: {
    run: returnExternalLabForCorrection,
    title: "Return for correction?",
    description:
      "The registration is sent back as Resubmission Required. Say what needs correcting.",
    confirmLabel: "Return for correction",
    confirmColor: "warning",
    input: "reason",
    inputLabel: "What needs correcting",
    done: "Lab returned for correction",
  },
  reject: {
    run: rejectExternalLab,
    title: "Reject this registration?",
    description:
      "Rejection is final: the lab cannot be edited, approved or mapped afterwards.",
    confirmLabel: "Reject",
    confirmColor: "error",
    input: "reason",
    inputLabel: "Reason for rejection",
    done: "Lab registration rejected",
  },
  resubmit: {
    run: resubmitExternalLab,
    title: "Resubmit for verification?",
    description:
      "Confirm the corrections are done. The lab returns to Pending Verification.",
    confirmLabel: "Resubmit",
    confirmColor: "primary",
    input: "note",
    done: "Lab resubmitted for verification",
  },
};

const InfoItem = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>{label}</Typography>
    <Typography sx={{ fontSize: 13.5, fontWeight: 600, wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Grid>
);

const ExternalLabDetailsDialog = ({ open, labId, onClose, onEdit, onChanged }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const fileInputRef = useRef(null);

  const [lab, setLab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!labId) return;
    setLoading(true);
    setError("");
    const res = await getExternalLab(labId);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      setLab(null);
      return;
    }
    setLab(res.data);
  }, [labId]);

  useEffect(() => {
    if (open) load();
    else setLab(null);
  }, [open, load]);

  const allowed = labActions(lab?.status);

  const runAction = async (text) => {
    const config = ACTIONS[action];
    setBusy(true);
    const res = await config.run(lab._id, text);
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    toast.success(config.done);
    setAction(null);
    setLab(res.data);
    onChanged?.();
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    const problem = documentFileError(file, lab?.documents?.length || 0);
    if (problem) {
      toast.error(problem);
      return;
    }
    setBusy(true);
    const res = await uploadExternalLabDocument(lab._id, file);
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    toast.success("Document uploaded");
    setLab(res.data);
  };

  const openDocument = async (doc) => {
    const res = await getExternalLabDocumentUrl(lab._id, doc._id);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    window.open(res.data.fileUrl, "_blank", "noopener,noreferrer");
  };

  const removeDocument = async (doc) => {
    setBusy(true);
    const res = await deleteExternalLabDocument(lab._id, doc._id);
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    toast.success("Document removed");
    setLab(res.data);
  };

  const config = action ? ACTIONS[action] : null;

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 17 }}>
            {lab?.labName || "External lab"}
          </Typography>
          {lab && <ExternalLabStatusChip status={lab.status} />}
        </Box>
        {lab && (
          <Typography component="span" sx={{ display: "block", fontSize: 12.5, color: "#6B7280" }}>
            Registered {formatDate(lab.createdAt)}
            {lab.resubmissionCount > 0 && ` · Resubmitted ${lab.resubmissionCount}×`}
            {lab.verifiedAt && ` · Verified ${formatDate(lab.verifiedAt)}`}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "#F9FAFB" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography role="alert" sx={{ color: "#B91C1C", fontWeight: 600 }}>
            {error}
          </Typography>
        ) : lab ? (
          <>
            {lab.statusReason &&
              [LAB_STATUS.REJECTED, LAB_STATUS.RESUBMISSION_REQUIRED].includes(lab.status) && (
                <Box
                  role="status"
                  data-testid="lab-status-reason"
                  sx={{
                    mb: 2,
                    p: 1.25,
                    borderRadius: 2,
                    border: "1px solid #FDE68A",
                    bgcolor: "#FFFBEB",
                    fontSize: 13,
                  }}
                >
                  <strong>
                    {lab.status === LAB_STATUS.REJECTED
                      ? "Rejection reason: "
                      : "Correction requested: "}
                  </strong>
                  {lab.statusReason}
                </Box>
              )}

            <Grid container spacing={2}>
              <InfoItem label="Lab Name" value={lab.labName} />
              <InfoItem label="Lab Owner Name" value={lab.ownerName} />
              <InfoItem label="Contact Number" value={lab.contactNumber} />
              <InfoItem label="Email" value={lab.email} />
              <InfoItem label="HFR ID" value={lab.hfrId} />
              <InfoItem label="State" value={lab.state} />
              <InfoItem label="District" value={lab.district} />
              <InfoItem label="City" value={lab.city} />
              <InfoItem label="PIN Code" value={lab.pincode} />
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
                  Full Address / Street Address
                </Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "pre-wrap" }}>
                  {lab.address || "—"}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                Supporting screenshots / documents
              </Typography>
              {allowed.canEdit && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={DOCUMENT_ACCEPT}
                    hidden
                    data-testid="lab-detail-document-input"
                    onChange={upload}
                  />
                  <Button
                    size="small"
                    startIcon={<AttachFileIcon />}
                    disabled={busy || (lab.documents?.length || 0) >= LAB_LIMITS.documentsMax}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Upload
                  </Button>
                </>
              )}
            </Box>
            {(lab.documents || []).length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
                No supporting documents attached.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {lab.documents.map((doc) => (
                  <Box
                    key={doc._id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      pl: 1.25,
                      pr: 0.5,
                      py: 0.25,
                      borderRadius: 2,
                      border: "1px solid #E5E7EB",
                      bgcolor: "#fff",
                      maxWidth: "100%",
                    }}
                  >
                    <Typography sx={{ fontSize: 12.5 }} noWrap title={doc.fileName}>
                      {doc.fileName}
                    </Typography>
                    <Tooltip title="Open">
                      <IconButton
                        size="small"
                        aria-label={`Open ${doc.fileName}`}
                        onClick={() => openDocument(doc)}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {allowed.canRemoveDocuments && (
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`Remove ${doc.fileName}`}
                          disabled={busy}
                          onClick={() => removeDocument(doc)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
              Verification history
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {[...(lab.history || [])].reverse().map((entry, index) => (
                <Box key={`${entry.action}-${index}`} sx={{ fontSize: 12.5 }}>
                  <Chip
                    size="small"
                    label={HISTORY_ACTION_LABELS[entry.action] || entry.action}
                    sx={{ height: 20, fontSize: 11, mr: 1 }}
                  />
                  <Typography component="span" sx={{ fontSize: 12, color: "#6B7280" }}>
                    {formatDate(entry.at)}
                  </Typography>
                  {entry.reason && (
                    <Typography sx={{ fontSize: 12.5, mt: 0.25, whiteSpace: "pre-wrap" }}>
                      {entry.reason}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            <ExternalLabMappingSection labId={lab._id} canMap={allowed.canMap} />
          </>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ textTransform: "none", color: "#6B7280", fontWeight: 600, mr: "auto" }}
        >
          Close
        </Button>
        {lab && allowed.canEdit && (
          <Button
            variant="outlined"
            onClick={() => onEdit(lab)}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Edit details
          </Button>
        )}
        {lab && allowed.canReject && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setAction("reject")}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Reject
          </Button>
        )}
        {lab && allowed.canReturnForCorrection && (
          <Button
            variant="outlined"
            color="warning"
            onClick={() => setAction("returnForCorrection")}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Return for correction
          </Button>
        )}
        {lab && allowed.canResubmit && (
          <Button
            variant="contained"
            onClick={() => setAction("resubmit")}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Resubmit for verification
          </Button>
        )}
        {lab && allowed.canApprove && (
          <Button
            variant="contained"
            color="success"
            onClick={() => setAction("approve")}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Approve
          </Button>
        )}
      </DialogActions>

      <ExternalLabActionDialog
        open={Boolean(config)}
        title={config?.title}
        description={config?.description}
        confirmLabel={config?.confirmLabel}
        confirmColor={config?.confirmColor}
        input={config?.input}
        inputLabel={config?.inputLabel}
        busy={busy}
        onCancel={() => setAction(null)}
        onConfirm={runAction}
      />
    </Dialog>
  );
};

export default ExternalLabDetailsDialog;
